// src/pages/Settingspage/Settings.jsx
import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { settingsService } from "../../services";
import "./Settings.css";

export default function Settings() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const fileRef = useRef(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // Settings state
  const [notifications, setNotifications] = useState({
    push: false,
    dnd: false,
  });
  const [theme, setTheme] = useState("light");

  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user profile from context or fetch
      if (user) {
        setForm({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
        });
      }

      // Load local preferences
      const savedTheme = settingsService.getTheme();
      const savedNotifications = settingsService.getNotificationPreferences();

      setTheme(savedTheme);
      document.documentElement.setAttribute('data-theme', savedTheme);
      setNotifications(savedNotifications);

    } catch (err) {
      console.error("Load settings error:", err);
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Update user profile from context when it changes
  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  // Save profile
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(false);

      await settingsService.updateProfile({
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
      });

      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (err) {
      console.error("Save profile error:", err);
      setError(err.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  // Handle avatar upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSaving(true);
      await settingsService.uploadAvatar(file);

      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (err) {
      console.error("Avatar upload error:", err);
      setError("Failed to upload avatar");
    } finally {
      setSaving(false);
    }
  };

  // Handle theme change
  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    settingsService.setTheme(newTheme);
  };

  // Handle notification preferences
  const handleNotificationChange = (key, value) => {
    const newPrefs = { ...notifications, [key]: value };
    setNotifications(newPrefs);
    settingsService.setNotificationPreferences(newPrefs);
  };

  // Handle password change
  const handleChangePassword = async () => {
    setPasswordError(null);

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }

    try {
      setChangingPassword(true);

      await settingsService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (err) {
      console.error("Change password error:", err);
      setPasswordError(err.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Get initials for avatar
  const getInitials = () => {
    const first = form.firstName?.[0] || "";
    const last = form.lastName?.[0] || "";
    return (first + last).toUpperCase() || "U";
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-loading">
          <div className="settings-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div className="mq-welcome-block">
        <button className="mq-back-btn" onClick={() => navigate('/dashboard')}>
          <span className="mq-back-btn-icon">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Back Home
        </button>
        <h1 className="mq-welcome-name">Settings</h1>
      </div>

      {/* Success message */}
      {saveSuccess && (
        <div className="settings-success">
          Settings saved successfully!
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="settings-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Personal Info */}
      <section className="settings-card">
        <div className="settings-card-head">Personal Information</div>

        <div className="settings-content">
          <div className="settings-form">
            <div className="row-2">
              <div className="field">
                <label>First Name</label>
                <input
                  value={form.firstName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, firstName: e.target.value }))
                  }
                  className="input"
                  placeholder="First name"
                />
              </div>
              <div className="field">
                <label>Last Name</label>
                <input
                  value={form.lastName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, lastName: e.target.value }))
                  }
                  className="input"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="row-2">
              <div className="field">
                <label>Email Address</label>
                <input value={form.email} disabled className="input" />
              </div>
              <div className="field">
                <label>Phone Number</label>
                <input
                  value={form.phone}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, phone: e.target.value }))
                  }
                  className="input"
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div className="field password-field">
              <label>Password</label>
              <div className="password-input-wrapper">
                <input
                  value="••••••••••••"
                  readOnly
                  className="input"
                  type="password"
                />
                <button
                  type="button"
                  className="btn-change"
                  onClick={() => setShowPasswordModal(true)}
                >
                  Change
                </button>
              </div>
            </div>

            <div className="settings-actions">
              <button
                className="btn-save"
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          {/* Avatar */}
          <div className="settings-avatar-section">
            <div className="avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt="User avatar" />
              ) : (
                <div className="avatar-placeholder">{getInitials()}</div>
              )}
            </div>
            <button className="btn-change-avatar" onClick={() => fileRef.current?.click()}>
              Change Avatar
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              hidden
            />
          </div>
        </div>
      </section>

      {/* System Settings */}
      <section className="settings-card">
        <div className="settings-card-head">System Settings</div>

        <div className="settings-row between">
          <div className="setting-block">
            <div className="setting-title">Notification Settings</div>
            <div className="setting-desc">Manage how you receive notifications</div>
          </div>
          <div className="setting-controls">
            <label className="check">
              <input
                type="checkbox"
                checked={notifications.push}
                onChange={(e) => handleNotificationChange("push", e.target.checked)}
              />
              <span>Push notifications</span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={notifications.dnd}
                onChange={(e) => handleNotificationChange("dnd", e.target.checked)}
              />
              <span>Do not disturb schedule</span>
            </label>
          </div>
        </div>

        <div className="divider" />

        <div className="settings-row between">
          <div className="setting-block">
            <div className="setting-title">Display Preferences</div>
            <div className="setting-desc">Choose your preferred theme</div>
          </div>
          <div className="setting-controls">
            <label className="radio">
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={theme === "dark"}
                onChange={(e) => handleThemeChange(e.target.value)}
              />
              <span>Dark mode</span>
            </label>
            <label className="radio">
              <input
                type="radio"
                name="theme"
                value="light"
                checked={theme === "light"}
                onChange={(e) => handleThemeChange(e.target.value)}
              />
              <span>Light mode</span>
            </label>
          </div>
        </div>
      </section>

      {/* Account Actions */}
      <section className="settings-card">
        <div className="settings-card-head">Account</div>

        <div className="settings-row between">
          <div className="setting-block">
            <div className="setting-title">Session Management</div>
            <div className="setting-desc">View and manage active sessions</div>
          </div>
          <button className="btn-secondary">Manage Sessions</button>
        </div>

        <div className="divider" />

        <div className="settings-row between">
          <div className="setting-block">
            <div className="setting-title danger">Delete Account</div>
            <div className="setting-desc">Permanently delete your account and all data</div>
          </div>
          <button className="btn-danger">Delete Account</button>
        </div>
      </section>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="settings-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h3>Change Password</h3>
              <button
                className="settings-modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
            </div>
            <div className="settings-modal-body">
              {passwordError && (
                <div className="settings-modal-error">{passwordError}</div>
              )}
              <div className="field">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))
                  }
                  className="input"
                  placeholder="Enter current password"
                />
              </div>
              <div className="field">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))
                  }
                  className="input"
                  placeholder="Enter new password"
                />
              </div>
              <div className="field">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))
                  }
                  className="input"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            <div className="settings-modal-footer">
              <button
                className="btn-save"
                onClick={handleChangePassword}
                disabled={changingPassword}
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
